// ============================================================
// YOM on YOM — the dashboard's own product analytics (Y-APP, browser SDK)
//
// Off unless the build sets VITE_YOM_SELF_PK (the Y-APP publishable key). When on:
//   - the SDK loads lazily (its own chunk) and tracks SPA page views (path only, no query);
//   - nothing at all with Do Not Track or Global Privacy Control, and never on client portals;
//   - before sign-in: cookieless (consent unknown); after sign-in: identified by the INTERNAL
//     user id only (never email or name), consent not persisted, no receipts, no advertising;
//   - superadmin sessions are marked internal (excluded from Y-APP reports);
//   - UI events pass the shared tracking-plan allowlist (SELF_EVENTS, writer 'client').
// docs/self-analytics.md.
// ============================================================

import { SELF_EVENTS, sanitizeSelfProps, type SelfClientEvent } from '@yourownmetric/shared'

type Sdk = typeof import('@yourownmetric/sdk')
type Client = InstanceType<Sdk['YOMClient']>

export interface SelfIdentity {
    userId: string
    orgId: string | null
    superAdmin: boolean
}

const PK = import.meta.env.VITE_YOM_SELF_PK
const ENDPOINT = import.meta.env.VITE_YOM_SELF_INGEST

/** DNT / GPC: the dashboard sends nothing at all (stricter than the SDK's cookieless mode). */
export function privacySignal(nav: Partial<Navigator> & { globalPrivacyControl?: boolean }) {
    return nav.doNotTrack === '1' || nav.globalPrivacyControl === true
}

export function selfAnalyticsAllowed(
    pk: string | undefined,
    nav: (Partial<Navigator> & { globalPrivacyControl?: boolean }) | undefined,
): boolean {
    return !!pk && /^yom_pk_/.test(pk) && !!nav && !privacySignal(nav)
}

let sdk: Promise<Sdk> | null = null
let client: Client | null = null
let clientInternal = false
let identified: string | null = null
let orgId: string | null = null
let queue: Promise<void> = Promise.resolve()

async function ensureClient(internal: boolean): Promise<Client> {
    sdk ??= import('@yourownmetric/sdk')
    const { YOMClient } = await sdk
    if (client && clientInternal !== internal) {
        // `internal` is fixed per SDK instance: a superadmin sign-in in this tab starts a new one.
        await client.destroy()
        client = null
        identified = null
    }
    if (!client) {
        client = new YOMClient().init({
            apiKey: PK!,
            ...(ENDPOINT ? { endpoint: ENDPOINT } : {}),
            internal,
            autoCapture: {
                pageviews: true,
                engagement: true,
                errors: false,
                performance: false,
                clicks: false,
                behavior: false,
                ecommerce: false,
            },
            consent: { default: 'unknown', persist: false, receipts: false },
            privacy: { respectDNT: true },
            eventBus: false,
        })
        clientInternal = internal
    }
    return client
}

/**
 * Start (or update) self-analytics for the current session: null = signed out. Safe to call
 * on every auth change; serialized, never throws.
 */
export function syncSelfAnalytics(who: SelfIdentity | null): void {
    if (!selfAnalyticsAllowed(PK, globalThis.navigator)) {
        return
    }
    queue = queue
        .then(async () => {
            const c = await ensureClient(!!who?.superAdmin)
            orgId = who?.orgId ?? null
            if (who && identified !== who.userId) {
                c.setConsent(
                    { analytics: 'granted', personalisation: 'denied', advertising: 'denied' },
                    { source: 'api', receipt: false },
                )
                c.identify(who.userId)
                identified = who.userId
            } else if (!who && identified) {
                c.reset()
                c.setConsent('unknown', { source: 'api', receipt: false })
                identified = null
            }
        })
        .catch(err => console.warn('[self-analytics] not started', err))
}

/** One dashboard UI event from the tracking plan; unknown / PII props are dropped. */
export function trackUi<E extends SelfClientEvent>(
    event: E,
    props: Partial<Record<keyof (typeof SELF_EVENTS)[E]['props'], string | number | boolean>> = {},
): void {
    if (!client || SELF_EVENTS[event].writer !== 'client') {
        return
    }
    try {
        const { props: clean } = sanitizeSelfProps(event, {
            ...props,
            ...(orgId ? { org_id: orgId } : {}),
            surface: 'dashboard',
        })
        client.track(event, clean)
    } catch {
        // analytics never breaks the UI
    }
}
